import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { OptionType } from "../../../../content/static/onboardingQuestions";
import { AppText, FlexDiv } from "../../../ui";
import { useOnboardingContext } from "../OnboardingContext";
import theme from "../../../../util/theme";
import { mobileSize } from "../../../../util/variables";
import { web_acq_question_click } from "../../../../services/mixpanel/acquisition-events";
import { getTextFromReactNode } from "../../../../util/misc";

interface SelectQuestionProps {
  id: string;
  title: string;
  subtitle?: string;
  preTitle?: string;
  options: OptionType[];
  answer_key: string;
  variant?: boolean;
}

interface SelectProps {
  label: string;
  description?: string;
  selected: boolean;
  updateChoice: (value: string, exists: boolean) => void;
  image?: string;
}

const SelectQuestion: React.FC<SelectQuestionProps> = ({
  id,
  title,
  subtitle,
  preTitle,
  options,
  answer_key,
  variant = false,
}) => {
  const [choice, setChoice] = useState("");
  const [popup, setPopup] = useState<{ message: string; image: string } | null>(null);
  const { updateOnboardingAnswer } = useOnboardingContext();

  useEffect(() => {
    if (!choice) {
      return;
    }

    const popup = options.find(option => option.label === choice)?.popup;
    if (popup) {
      setPopup(popup);
    }

    const choiceText = getTextFromReactNode(choice);
    updateOnboardingAnswer(answer_key, choiceText);
  }, [choice, answer_key]);

  const updateChoice = React.useCallback(
    (value: string) => {
      setChoice(value);
      web_acq_question_click(id);
    },
    [id],
  );

  return (
    <FlexDiv
      direction="column"
      align={variant ? "flex-start" : "center"}
      style={{ position: "relative", overflow: "hidden", height: "100%" }}
    >
      <TextWrapper>
        {preTitle && (
          <AppText
            noMargin
            fontSize={16}
            mobileFontSize={14}
            textAlign={variant ? "left" : "center"}
            style={{ marginBottom: 4, maxWidth: 468 }}
          >
            {preTitle}
          </AppText>
        )}
        <AppText
          noMargin
          fontSize={24}
          mobileFontSize={20}
          fontWeight={500}
          style={{ maxWidth: 468 }}
          textAlign={variant ? "left" : "center"}
        >
          {title}
        </AppText>
        {subtitle && <Subtitle textAlign={variant ? "left" : "center"}>{subtitle}</Subtitle>}
      </TextWrapper>
      <OptionsContainer>
        {options.length > 0 &&
          options.map(option => (
            <Select
              key={option.label}
              label={option.label}
              description={option.description}
              image={option.image}
              updateChoice={updateChoice}
              selected={choice === option.label}
            />
          ))}
        <Shadow />
      </OptionsContainer>
      {popup && (
        <PopupContainer>
          <PopupContent align="center" justify="flex-start" gap={16}>
            <img
              src={popup.image}
              alt={popup.message}
              width={73}
              height={71}
            />
            <AppText noMargin fontSize={18} mobileFontSize={18} textAlign="left" style={{ maxWidth: 300 }}>
              {popup.message}
            </AppText>
          </PopupContent>
        </PopupContainer>
      )}
    </FlexDiv>
  );
};

const TextWrapper = styled.div`
  padding: 0 4px;
  @media ${mobileSize} {
    padding: 0 32px;
  }
`;

const Subtitle = styled(AppText as any).attrs({
  fontSize: 16,
  mobileFontSize: 14,
})`
  max-width: 468px;
  margin-bottom: 0px;
  margin-top: 16px;
  @media ${mobileSize} {
    margin-top: 8px;
  }
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-grow: 1;
  gap: 24px;
  margin-top: 60px;
  padding: 12px 6px;
  height: 100%;
  max-width: 100vw;
  overflow: auto;
  @media ${mobileSize} {
    margin-top: 24px;
    padding: 8px 32px;
    max-height: calc(100% - 60px); // height of text + margins
    gap: 10px;
  }
`;

const Shadow = styled.div`
  display: none;
  position: absolute;
  bottom: 0;
  left: 0;
  height: 5px;
  width: 100vw;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.1) 0%, rgba(255, 255, 255, 0) 100%);
  @media ${mobileSize} {
    display: block;
  }
`;

const PopupContainer = styled(FlexDiv)`
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  max-width: 468px;
  padding: auto 12px 40px 12px;
  margin: 0 6px;
  @media ${mobileSize} {
    padding: 24px 32px;
    margin: 0;
  }
`;

const PopupContent = styled(FlexDiv)`
  width: 100%;
  padding: 16px 14px;
  border: 2px solid #FFF48F;
  border-radius: 8px;

  background-color: #FFF48F66;
  @media ${mobileSize} {
    padding: 8px 14px;
  }
`;

const Select: React.FC<SelectProps> = ({ label, description, image, updateChoice, selected }) => {
  return (
    <Container selected={selected} hasImage={!!image} onClick={() => updateChoice(label, selected)}>
      {image && <Img src={image} alt={`${label} icon`} width={56} height={56} />}
      <AppText noMargin textAlign={description ? "left" : "center"} mobileFontSize={16} style={{ width: "100%" }}>
        {label}
        {description && <Description>{description}</Description>}
      </AppText>
    </Container>
  );
};

const Container = styled.div<{ selected: boolean; hasImage: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 468px;
  flex-shrink: 0;
  max-width: 100%;
  height: 86px;
  padding: ${props => (props.hasImage ? "24px 16px 24px 0px" : "24px 16px")};
  background-color: ${props => (props.selected ? theme.SELECTED_COLOR : theme.WHITE_COLOR)};
  outline: ${props => (props.selected ? `1px solid ${theme.SELECTED_BORDER_COLOR}` : "none")};
  border-radius: 8px;
  box-shadow: ${props =>
    props.selected ? "0px 1px 7px rgba(44, 39, 56, 0.14)" : "0px 2px 11px rgba(44, 39, 56, 0.14)"};
  cursor: pointer;
  @media ${mobileSize} {
    height: 64px;
    min-height: 64px;
  }
`;

const Img = styled.img`
  width: 127px;
  height: inherit;
  padding: 4px;
`;

const Description = styled.span`
  display: block;
  font-family: ${theme.SECONDARY_FONT};
  font-size: 16px;
  color: ${theme.SLATE_COLOR_2};
  margin: 0;
  
  @media ${mobileSize} {
    font-size: 14px;
  }
`;

export default SelectQuestion;
